import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Play } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const useList = (entity) =>
  useQuery({ queryKey: ["entity", entity], queryFn: async () => (await api.get(`/${entity}`)).data });

export default function StartOnBehalf() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ job_id: "", machine_id: "", operation_id: "", operator_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v, ...(k === "job_id" ? { machine_id: "", operation_id: "", operator_id: "" } : {}) }));

  const { data: jobs = [] } = useList("jobs");
  const { data: machines = [] } = useList("machines");
  const { data: operations = [] } = useList("operations");
  const { data: team = [] } = useList("team");
  const { data: sessions = [] } = useQuery({
    queryKey: ["work-sessions"],
    queryFn: async () => (await api.get("/work/sessions")).data,
  });

  const activeJobs = useMemo(() => jobs.filter((j) => j.job_status === "Active"), [jobs]);
  const job = activeJobs.find((j) => j.id === f.job_id);
  const plan = job?.machine_plan || [];
  const planRow = plan.find((r) => r.machine_id === f.machine_id);

  const machineOptions = plan.length
    ? machines.filter((m) => plan.some((r) => r.machine_id === m.id))
    : machines;
  const opOptions = (job?.operations_required || []).length
    ? operations.filter((o) => (job.operations_required || []).includes(o.id))
    : operations;
  const operatorOptions = (planRow?.operator_ids || []).length
    ? team.filter((t) => (planRow.operator_ids || []).includes(t.id))
    : team;

  const engaged = new Set(sessions.filter((s) => ["Running", "Paused"].includes(s.status)).map((s) => s.operator_id));
  const ready = f.job_id && f.machine_id && f.operation_id && f.operator_id;

  const startNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/work/start", f);
      const opName = team.find((t) => t.id === f.operator_id)?.name || "operator";
      toast.success(`Timer ${data.code} started for ${opName}`);
      setOpen(false);
      setF({ job_id: "", machine_id: "", operation_id: "", operator_id: "" });
      qc.invalidateQueries({ queryKey: ["work-sessions"] });
      qc.invalidateQueries({ queryKey: ["work-assignments"] });
      qc.invalidateQueries({ queryKey: ["entity", "production"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        onClick={() => setOpen(true)} data-testid="start-on-behalf-btn">
        <Play className="h-4 w-4 mr-1.5" weight="fill" /> Start on behalf
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="start-on-behalf-dialog">
          <DialogHeader>
            <DialogTitle>Start a log entry on behalf of an operator</DialogTitle>
            <DialogDescription>
              Pick any Active job from Job Master. The timer runs under the operator's name and saves the hours into the
              Daily Production Log when it ends.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Active job</Label>
              <Select value={f.job_id} onValueChange={(v) => set("job_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="sob-job"><SelectValue placeholder={`${activeJobs.length} active jobs`} /></SelectTrigger>
                <SelectContent>
                  {activeJobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.code} · {j.job_name} — {j.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Machine</Label>
                <Select value={f.machine_id} onValueChange={(v) => set("machine_id", v)} disabled={!f.job_id}>
                  <SelectTrigger className="mt-1.5" data-testid="sob-machine"><SelectValue placeholder="Select machine" /></SelectTrigger>
                  <SelectContent>
                    {machineOptions.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.machine_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Operation</Label>
                <Select value={f.operation_id} onValueChange={(v) => set("operation_id", v)} disabled={!f.job_id}>
                  <SelectTrigger className="mt-1.5" data-testid="sob-operation"><SelectValue placeholder="Select operation" /></SelectTrigger>
                  <SelectContent>
                    {opOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.code} · {o.operation_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Operator / machinist</Label>
              <Select value={f.operator_id} onValueChange={(v) => set("operator_id", v)} disabled={!f.job_id}>
                <SelectTrigger className="mt-1.5" data-testid="sob-operator"><SelectValue placeholder="Select operator" /></SelectTrigger>
                <SelectContent>
                  {operatorOptions.map((t) => (
                    <SelectItem key={t.id} value={t.id} disabled={engaged.has(t.id)}>
                      {t.code} · {t.name}{engaged.has(t.id) ? " — already running" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {planRow?.planned_hours ? (
                <Badge variant="outline" className="mt-2 bg-slate-50 text-slate-600">
                  Machine planned {planRow.planned_hours} h on this job
                </Badge>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="sob-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!ready || busy} onClick={startNow}
              data-testid="sob-start">{busy ? "Starting…" : "Start timer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
